import { Injectable, OnDestroy } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  ObservableNotification,
  Subscription,
  throwError,
} from 'rxjs';
import {
  map,
  take,
  filter,
  switchMap,
  catchError,
  distinctUntilChanged,
  materialize,
  dematerialize,
} from 'rxjs/operators';

import { AppState, ConfigService, mapErrorForSentry, SentryLogger, unsubscribe } from '@fcom/core';
import { Profile } from '@fcom/core-api/login';
import { profile, loginToken } from '@fcom/core/selectors';
import { finShare, snapshot } from '@fcom/rx';
import { StatusCode } from '@fcom/common';

export enum TwoFactorAuthStatus {
  INITIAL = 'initial',
  LOADING = 'loading',
  DISABLED = 'disabled',
  ENABLED = 'enabled',
  ERROR = 'error',
}

export type TwoFactorAuthEnabledStatus =
  | TwoFactorAuthStatus.INITIAL
  | TwoFactorAuthStatus.ENABLED
  | TwoFactorAuthStatus.DISABLED;

export interface EnableGAuthValidatedResponse {
  secretKey: string;
}

export enum TwoFactorAuthType {
  EMAIL = 'email',
  SMS = 'sms',
  APP = 'app',
}

export type TwoFactorAuthLastUpdateStatusData = {
  status?: TwoFactorAuthStatus;
  type?: TwoFactorAuthType;
};

export type TwoFactorAuthEnabledStatusData = {
  status?: TwoFactorAuthEnabledStatus;
  type?: TwoFactorAuthType;
  isEnabled: boolean;
};

export interface GetGAuthStatusResponse {
  secretKey?: string;
  validationCode: number;
  scratchCodes?: number[];
  smsAuthenticator: boolean;
  validated?: boolean;
  emailAuthenticator?: boolean;
}

export interface ValidateGAuthResponse {
  valid: boolean;
}

export type Validate2faEmailVerificationCodeRequest = {
  token: string;
};

export type Validate2faEmailVerificationCodeErrorResponse = {
  status: {
    message: StatusCode.ERROR;
  };
  reason: string;
};

export function isValidate2faEmailVerificationCodeErrorResponse(
  error: unknown
): error is Validate2faEmailVerificationCodeErrorResponse {
  return typeof error === 'object' && error !== null && 'reason' in error;
}

const STATUS_PATH = 'getgauth';
const ENABLE_SMS_PATH = 'enablesmsauth';
const ENABLE_VALIDATED_PATH = 'enablegauthvalidated';
const VALIDATE_PATH = 'validategauth';
const DISABLE_PATH = 'disablegauth';

@Injectable()
export class TwoFactorAuthService implements OnDestroy {
  private subscriptions = new Subscription();
  private memberNumber$: Observable<string>;

  private setLastUpdateStatus$ = new BehaviorSubject<TwoFactorAuthLastUpdateStatusData>({
    status: TwoFactorAuthStatus.INITIAL,
  });
  private setEnabledStatus$ = new BehaviorSubject<TwoFactorAuthEnabledStatusData>({
    status: TwoFactorAuthStatus.INITIAL,
    isEnabled: false,
  });

  private getStatus$ = new BehaviorSubject(false);
  private getStatusRequest$: Observable<ObservableNotification<GetGAuthStatusResponse>>;

  private enableSMSAuth$ = new BehaviorSubject(false);
  private enableSMSAuthRequest$: Observable<ObservableNotification<unknown>>;

  private validateGAuth$ = new BehaviorSubject(false);
  private validateGAuthRequest$: Observable<ValidateGAuthResponse>;

  private validate2faEmailVerificationCode$ = new BehaviorSubject(false);
  private validate2faEmailVerificationCodeRequest$: Observable<unknown>;

  private disableGAuth$ = new BehaviorSubject(false);
  private disableGAuthRequest$: Observable<ObservableNotification<unknown>>;

  lastUpdateStatus$ = this.setLastUpdateStatus$.pipe(distinctUntilChanged(), finShare());

  enabledStatus$ = this.setEnabledStatus$.asObservable();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private store$: Store<AppState>,
    private sentryLogger: SentryLogger
  ) {
    this.memberNumber$ = this.store$.pipe(
      profile(),
      take(1),
      map((memberProfile: Profile) => memberProfile.memberNumber)
    );

    this.subscriptions.add(
      this.lastUpdateStatus$
        .pipe(filter(({ status }) => status === TwoFactorAuthStatus.ENABLED || status === TwoFactorAuthStatus.DISABLED))
        .subscribe({
          next: ({ status, type }) => {
            this.setEnabledStatus$.next({
              status: status as TwoFactorAuthStatus.ENABLED | TwoFactorAuthStatus.DISABLED,
              type,
              isEnabled: status === TwoFactorAuthStatus.ENABLED,
            });
          },
        })
    );

    this.subscriptions.add(
      merge(
        this.getStatus$,
        this.enableSMSAuth$,
        this.validateGAuth$,
        this.validate2faEmailVerificationCode$,
        this.disableGAuth$
      ).subscribe({
        next: () => {
          this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.LOADING });
        },
      })
    );

    this.getStatusRequest$ = this.getStatus$.pipe(
      filter(Boolean),
      switchMap(() => this.gAuthRequest<GetGAuthStatusResponse>(STATUS_PATH).pipe(materialize())),
      finShare({ bufferSize: 2, refCount: true })
    );

    this.subscriptions.add(
      this.getStatusRequest$.subscribe({
        next: (notification) => {
          if (notification.kind === 'N') {
            const response = notification.value;
            const appAuthenticator = response.secretKey && response.validated !== false;
            if (response.emailAuthenticator || response.smsAuthenticator || appAuthenticator) {
              this.setLastUpdateStatus$.next({
                status: TwoFactorAuthStatus.ENABLED,
                type: appAuthenticator
                  ? TwoFactorAuthType.APP
                  : response.emailAuthenticator
                    ? TwoFactorAuthType.EMAIL
                    : TwoFactorAuthType.SMS,
              });
            } else {
              this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.DISABLED });
            }
          } else if (notification.kind === 'E') {
            this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.ERROR });
          }
        },
      })
    );

    this.enableSMSAuthRequest$ = this.enableSMSAuth$.pipe(
      filter(Boolean),
      switchMap(() => this.gAuthRequest<unknown>(ENABLE_SMS_PATH).pipe(materialize())),
      finShare({ bufferSize: 2, refCount: true })
    );

    this.subscriptions.add(
      this.enableSMSAuthRequest$.subscribe({
        next: (notification) => {
          if (notification.kind === 'N') {
            this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.ENABLED, type: TwoFactorAuthType.SMS });
          } else if (notification.kind === 'E') {
            this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.ERROR });

            this.sentryLogger.error('Error enabling SMS MFA', {
              error: mapErrorForSentry(notification.error),
            });
          }
        },
      })
    );

    this.disableGAuthRequest$ = this.disableGAuth$.pipe(
      filter(Boolean),
      switchMap(() => this.gAuthRequest<unknown>(DISABLE_PATH).pipe(materialize())),
      finShare({ bufferSize: 2, refCount: true })
    );

    this.subscriptions.add(
      this.disableGAuthRequest$.subscribe({
        next: (notification) => {
          if (notification.kind === 'N') {
            this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.DISABLED });
          } else if (notification.kind === 'E') {
            this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.ERROR });

            this.sentryLogger.error('Error disabling GAuth', {
              error: mapErrorForSentry(notification.error),
            });
          }
        },
      })
    );

    this.getStatus$.next(true);
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  getStatus(): Observable<GetGAuthStatusResponse> {
    this.getStatus$.next(true);
    return this.getStatusRequest$.pipe(dematerialize(), take(1));
  }

  enableSMSAuth(): Observable<unknown> {
    this.enableSMSAuth$.next(true);
    return this.enableSMSAuthRequest$.pipe(dematerialize(), take(1));
  }

  enableGAuthValidated(): Observable<EnableGAuthValidatedResponse> {
    return this.gAuthRequest<EnableGAuthValidatedResponse>(ENABLE_VALIDATED_PATH).pipe(
      catchError((error) => {
        this.sentryLogger.error('Error enabling GAuth Validated', {
          error: mapErrorForSentry(error),
        });

        return throwError(() => error);
      })
    );
  }

  getQRCodeImageUrl(secretKey: string): Observable<string> {
    return this.memberNumber$.pipe(
      map(
        (memberNumber) =>
          `https://${this.configService.cfg.casHost}/cas/gauth/qrgen?key=otpauth://totp/www.finnair.com${
            this.configService.cfg.casEnv === 'preprod' ? '(preprod)' : ''
          }:${memberNumber}?secret=${secretKey}&issuer=Finnair`
      )
    );
  }

  validateGAuth(verificationCode: string): Observable<ValidateGAuthResponse> {
    this.validateGAuth$.next(false);

    this.validateGAuthRequest$ = this.validateGAuth$.pipe(
      filter(Boolean),
      switchMap(() => {
        return this.gAuthRequest<ValidateGAuthResponse>(`${VALIDATE_PATH}/${verificationCode}`);
      }),
      finShare()
    );

    this.subscriptions.add(
      this.validateGAuthRequest$.pipe(take(1)).subscribe({
        next: (response) => {
          if (response.valid) {
            this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.ENABLED, type: TwoFactorAuthType.APP });
          } else {
            this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.ERROR });
          }
        },
        error: (error) => {
          this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.ERROR });

          this.sentryLogger.error(
            'Error in the process of enabling app MFA - validating the 2fa app verification code has failed',
            {
              error: mapErrorForSentry(error),
            }
          );
        },
      })
    );

    this.validateGAuth$.next(true);
    return this.validateGAuthRequest$.pipe(take(1));
  }

  send2faEmailVerificationCode(): Observable<unknown> {
    return this.store$.pipe(
      loginToken(),
      take(1),
      switchMap((token) => {
        const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileEmailVerifyUrl}`;
        const apiKey = this.configService.cfg.loyaltyServices.legacy.apiKey;

        const httpOptions = {
          headers: new HttpHeaders({
            Authorization: `Bearer ${token}`,
            'x-api-key': apiKey,
          }),
        };

        return this.http.post<void>(apiUrl, null, httpOptions).pipe(
          catchError((error: unknown) => {
            this.sentryLogger.error(
              'Error in the process of enabling email MFA - sending 2fa email verification code has failed',
              {
                error: mapErrorForSentry(error),
              }
            );

            return throwError(() => error);
          })
        );
      })
    );
  }

  validate2faEmailVerificationCode(verificationCode: string): Observable<unknown> {
    this.validate2faEmailVerificationCode$.next(false);

    this.validate2faEmailVerificationCodeRequest$ = this.validate2faEmailVerificationCode$.pipe(
      filter(Boolean),
      switchMap(() => {
        const apiUrl = `${this.configService.cfg.loyaltyServices.legacy.loyaltyProfileEmailConfirmUrl}`;

        const httpOptions = {
          headers: this.getLoyaltyServicesHttpHeaders(),
        };

        return this.http.post(
          apiUrl,
          { token: verificationCode } as Validate2faEmailVerificationCodeRequest,
          httpOptions
        );
      }),
      finShare()
    );

    this.subscriptions.add(
      this.validate2faEmailVerificationCodeRequest$.pipe(take(1)).subscribe({
        next: () => {
          this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.ENABLED, type: TwoFactorAuthType.EMAIL });
        },
        error: (error: unknown) => {
          this.setLastUpdateStatus$.next({ status: TwoFactorAuthStatus.ERROR });

          this.sentryLogger.error(
            'Error in the process of enabling email MFA - validating the 2fa email verification code has failed',
            {
              error: mapErrorForSentry(error),
            }
          );
        },
      })
    );

    this.validate2faEmailVerificationCode$.next(true);
    return this.validate2faEmailVerificationCodeRequest$.pipe(take(1));
  }

  disableGAuth(): Observable<unknown> {
    this.disableGAuth$.next(true);
    return this.disableGAuthRequest$.pipe(dematerialize(), take(1));
  }

  private gAuthRequest<ResponseType>(path: string): Observable<ResponseType> {
    const loginToken$ = this.store$.pipe(loginToken(), filter(Boolean), take(1));
    return combineLatest([loginToken$, this.memberNumber$]).pipe(
      switchMap(([token, memberNumber]) => {
        const httpOptions = {
          headers: new HttpHeaders({
            oauth_token: token,
            'x-api-key': this.configService.cfg.loyaltyServices.memberService.gauthApiKey,
          }),
        };
        return this.http.get<ResponseType>(
          `${this.configService.cfg.loyaltyServices.memberService.gauthUrl}/${path}/${memberNumber}?_=${Date.now()}`,
          httpOptions
        );
      }),
      catchError((error: unknown) => {
        this.sentryLogger.warn('Error submitting GAuth request', { error: mapErrorForSentry(error) });
        return throwError(() => error);
      })
    );
  }

  private getLoyaltyServicesHttpHeaders(): HttpHeaders {
    const token = snapshot(this.store$.pipe(loginToken()));
    const apiKey = this.configService.cfg.loyaltyServices.legacy.apiKey;

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'x-api-key': apiKey,
    });
  }
}
